import React from 'react';
import { Link } from 'react-router-dom';
import { getOrigins } from '../utils/host';

const formatRole = (role) => {
  if (role === 'role-acct-mgmt') return 'Account Management';
  if (role === 'role-key-mgmt') return 'Key Management';
  if (role === 'role-support') return 'Support';
  return role;
};

const formatApiHistoryLimit = (value) => {
  if (value === null || value === undefined) return 'Not set';
  if (value === 0) return 'Latest year only';
  if (value >= 999) return 'All history';
  return `Up to ${value} year${value === 1 ? '' : 's'} back`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return 'Not set';
  return Number(value).toLocaleString();
};

const formatApiLimit = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  return Number(value).toLocaleString();
};

const formatApiSourceLimit = (value) => {
  if (value === null || value === undefined || value === '') return 'All data sources';
  return value;
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
};
const formatAccessThroughDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  if (
    parsed.getUTCHours() === 0 &&
    parsed.getUTCMinutes() === 0 &&
    parsed.getUTCSeconds() === 0 &&
    parsed.getUTCMilliseconds() === 0
  ) {
    parsed.setUTCDate(parsed.getUTCDate() - 1);
  }
  return parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
};
const formatCadence = (value) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
};
const formatMoney = (cents) => {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const AccountOverview = ({ accountData, orgUsers, apiPermissions, canManageAccount }) => {
  const { opsOrigin } = getOrigins();
  const roleList = (accountData.roles || []).filter(Boolean);
  const orgTypeLabel = accountData.org_type === 'type-org' ? 'Organization' : 'Individual';
  const docsUrl = accountData.docs_url || 'https://finbaseco.com/docs';
  const supportUrl = accountData.support_url || 'https://support.finbaseco.com';
  const apiRequestsCurrent = apiPermissions?.api_requests_current ?? 0;
  const apiRateLimit = apiPermissions?.api_rate_limit ?? null;
  const apiRemaining = apiRateLimit !== null ? Math.max(apiRateLimit - apiRequestsCurrent, 0) : null;
  const subscription = accountData.subscription || null;
  const hasScheduledCancellation = Boolean(subscription?.scheduled_cancellation) || subscription?.raw_status === 'scheduled_cancel';
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="h4 fw-bold mb-1">Welcome back, {accountData.full_name}</h2>
          <div className="text-muted">Here’s a quick snapshot of your account.</div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="card-title d-flex align-items-center justify-content-between">
                <h5 className="mb-0">My Profile</h5>
                {accountData.is_internal && accountData.org_internal && (
                  <span className="badge bg-info-subtle text-dark border">Internal</span>
                )}
              </div>
              <div className="mb-2"><strong>Name:</strong> {accountData.full_name}</div>
              <div className="mb-2"><strong>Email:</strong> {accountData.email}</div>
              <div className="mb-3">
                <strong>Roles:</strong>{' '}
                {roleList.length === 0 && 'None'}
                {roleList.length > 0 && (
                  <>
                    {roleList.map((role, index) => {
                      const label = formatRole(role);
                      if (role === 'role-support') {
                        return (
                          <span key={role}>
                            <a
                              href={`${opsOrigin}/admin`}
                              className="text-primary text-decoration-none"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {label} <i className="fas fa-external-link-alt ms-1" aria-hidden="true"></i>
                            </a>
                            {index < roleList.length - 1 ? ', ' : ''}
                          </span>
                        );
                      }
                      return (
                        <span key={role}>
                          {label}
                          {index < roleList.length - 1 ? ', ' : ''}
                        </span>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {accountData.roles?.includes('role-acct-mgmt') && (
          <>
            <div className="col-md-6 col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">Organization</h5>
                  <p className="mb-1"><strong>Name:</strong> {accountData.org_name}</p>
                  <p className="mb-1"><strong>Type:</strong> {orgTypeLabel}</p>
                  <p className="mb-1"><strong>Contact:</strong> {accountData.org_email || 'Not set'}</p>
                  <p className="mb-1">
                    <strong>MFA:</strong> {accountData.mfa_required ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">Users</h5>
                  <p className="display-6 mb-1">{orgUsers.length}</p>
                  <div className="text-muted">Active users tied to this organization.</div>
                </div>
              </div>
            </div>
          </>
        )}
        {(() => {
          const roles = accountData.roles || [];
          const supportOnly = roles.includes('role-support') && roles.length === 1;
          return !supportOnly;
        })() && (
          <div className="col-md-6 col-lg-4">
            <div className="card h-100">
              <div className="card-body">
                <div className="card-title d-flex align-items-start justify-content-between gap-3">
                  <h5 className="mb-0">API Permissions</h5>
                  <a
                    href={docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm flex-shrink-0"
                  >
                    Open API Docs <i className="fas fa-external-link-alt ms-1" aria-hidden="true"></i>
                  </a>
                </div>
                <div className="text-muted small fw-semibold mb-2">API Quotas</div>
                <div className="mb-2">
                  <strong>Monthly limit:</strong> {formatApiLimit(apiPermissions?.api_rate_limit)}
                </div>
                <div className="mb-2">
                  <strong>Requests this month:</strong> {formatNumber(apiRequestsCurrent)}
                </div>
                <div className="mb-3">
                  <strong>Remaining:</strong> {apiRemaining === null ? 'Unlimited' : formatNumber(apiRemaining)}
                </div>
                <div className="text-muted small mb-3">
                  Usage figures are near real-time and may be delayed by a few minutes.
                </div>
                <div className="text-muted small fw-semibold mb-2">Data Accessibility</div>
                <div className="mb-2">
                  <strong>Data sources:</strong> {formatApiSourceLimit(apiPermissions?.api_src_limit)}
                  <div className="text-muted small">Datasets your org can query. This list can expand as access changes.</div>
                </div>
                <div className="mb-2">
                  <strong>Derived data:</strong> {apiPermissions?.api_src_derived_flag ? 'Allowed' : 'Not allowed'}
                  <div className="text-muted small">Computed datasets based on underlying sources (premium feature).</div>
                </div>
                <div className="mb-0">
                  <strong>History access:</strong> {formatApiHistoryLimit(apiPermissions?.api_hist_limit)}
                  <div className="text-muted small">How far back your org can query historical data.</div>
                </div>
                <div className="text-muted small mt-2">Permissions reflect your organization’s current access.</div>
              </div>
            </div>
          </div>
        )}
        <div className="col-md-6 col-lg-4">
          <div className="card h-100">
            <div className="card-body">
              <div className="card-title d-flex align-items-start justify-content-between gap-3">
                <h5 className="mb-0">Support</h5>
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm flex-shrink-0"
                >
                  Open Support Portal <i className="fas fa-external-link-alt ms-1" aria-hidden="true"></i>
                </a>
              </div>
              <p className="text-muted small mb-3">
                Use the Finbase Support Portal to submit and track requests for your organization.
              </p>
              <div className="text-muted small">
                Support tickets and request history are managed through the support portal.
              </div>
            </div>
          </div>
        </div>
        {canManageAccount && (
          <div className="col-md-6 col-lg-4">
            <div className="card h-100">
              <div className="card-body">
                <div className="card-title d-flex align-items-start justify-content-between gap-3">
                  <h5 className="mb-0">Subscription</h5>
                  <Link className="btn btn-outline-primary btn-sm flex-shrink-0" to="/account/subscription">
                    Manage Subscription
                  </Link>
                </div>
                {subscription?.is_internal ? (
                  <p className="text-muted mb-3">Internal organization. Subscription billing does not apply.</p>
                ) : (
                  <>
                    <div className="mb-1"><strong>Plan:</strong> {subscription?.plan_name || 'No Active Subscription'}</div>
                    <div className="mb-1"><strong>Status:</strong> {String(subscription?.status || 'none').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</div>
                    <div className="mb-1">
                      <strong>{subscription?.billing_cadence === 'annual' ? 'Annual Price:' : 'Monthly Price:'}</strong>{' '}
                      {subscription?.billing_cadence === 'annual'
                        ? formatMoney(subscription?.price_annual_cents)
                        : formatMoney(subscription?.price_monthly_cents)}
                    </div>
                    <div className="text-muted small mb-1">Plus tax, where applicable.</div>
                    <div className="mb-3">
                      <strong>
                        {subscription?.status === 'trialing'
                          ? 'Trial Ends:'
                          : hasScheduledCancellation
                            ? 'Access Through:'
                            : 'Next Renewal / Charge Date:'}
                      </strong>{' '}
                      {subscription?.status === 'trialing'
                        ? formatAccessThroughDate(subscription?.trial_end)
                        : hasScheduledCancellation
                          ? formatAccessThroughDate(subscription?.billing_period_end)
                          : formatDate(subscription?.billing_period_end)}
                    </div>
                    {subscription?.scheduled_change && (
                      <div className="small text-muted mb-3">
                        Scheduled change: {subscription.scheduled_change.plan_name} / {formatCadence(subscription.scheduled_change.billing_cadence)} on {formatDate(subscription.scheduled_change.effective_on)}. Displayed pricing elsewhere reflects the current rate; the rate in effect on the conversion date will apply.
                      </div>
                    )}
                    {subscription?.grandfather_sunset && (
                      <div className="small text-muted mb-3">
                        Pricing update: your current plan pricing updates on {formatDate(subscription.grandfather_sunset.effective_change_date || subscription.grandfather_sunset.grandfather_end_date)}
                        {subscription.grandfather_sunset.price_changes
                          ? `, changing the ${formatCadence(subscription.grandfather_sunset.billing_cadence)} price from ${formatMoney(subscription.grandfather_sunset.current_price_cents)} to ${formatMoney(subscription.grandfather_sunset.target_price_cents)}.`
                          : '.'}
                      </div>
                    )}
                    {subscription?.scheduled_cancellation && (
                      <div className="small text-muted mb-3">
                        Scheduled cancellation: access through {formatAccessThroughDate(subscription.scheduled_cancellation.access_through)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountOverview;
